import React from 'react'
import {useEvent} from 'Event/EventProvider'
import {ValidationError} from 'lib/ui/api-client'
import {api} from 'lib/url'
import {Area} from 'organization/Event/AreasProvider'
import {useOrganization} from 'organization/OrganizationProvider'
import {useState} from 'react'
import {useForm} from 'react-hook-form'
import Page from 'lib/ui/layout/Page'
import withStyles from '@material-ui/core/styles/withStyles'
import {spacing} from 'lib/ui/theme'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import {fieldError} from 'lib/form'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import Button from '@material-ui/core/Button'
import {useEventRoutes} from 'organization/Event/EventRoutes'
import {useHistory} from 'react-router-dom'
import {areaRoutes} from 'organization/Event/Area/AreaRoutes'
import {ZoomAreasBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'
import {useQueryClient} from 'react-query'

interface CreateAreaData {
  name: string
  requires_approval: boolean
  allows_multiple_devices: boolean
  zoom_enable_q_and_a: boolean
  zoom_enable_language_interpretation: boolean
  zoom_enable_sign_language_interpretation: boolean
}

export default function CreateAreaForm() {
  const {register, errors, handleSubmit} = useForm()
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<
    ValidationError<CreateAreaData>
  >(null)
  const history = useHistory()
  const createArea = useCreateArea()
  const eventRoutes = useEventRoutes()
  const {event} = useEvent()
  const queryClient = useQueryClient()

  const goToArea = (area: Area) => {
    history.push(areaRoutes({area, eventRoutes}).root)
  }

  const submit = (data: CreateAreaData) => {
    setSubmitting(true)
    createArea(data)
      .then((newArea: Area) => {
        queryClient.invalidateQueries(['events', event.id, 'areas'])
        goToArea(newArea)
      })
      .catch((e) => {
        setServerError(e)
        setSubmitting(false)
      })
  }

  const nameError = fieldError('name', {form: errors, response: serverError})

  return (
    <ZoomAreasBreadcrumbs page="Create">
      <Page>
        <Title variant="h5" align="center">
          Create Area
        </Title>
        <form onSubmit={handleSubmit(submit)}>
          <TextField
            label="Area Name"
            name="name"
            required
            fullWidth
            variant="outlined"
            inputProps={{
              ref: register({
                required: 'Name is required',
              }),
              'aria-label': 'area name input',
            }}
            error={Boolean(nameError)}
            helperText={nameError}
          />
          <FormControl required component="fieldset" fullWidth>
            <FormControlLabel
              inputRef={register}
              name="requires_approval"
              aria-label="toggle requires approval to join meeting"
              control={<Checkbox defaultChecked={true} />}
              label="Requires approval to join area meetings?"
            />
          </FormControl>
          <FormControl required component="fieldset" fullWidth>
            <FormControlLabel
              inputRef={register}
              name="allows_multiple_devices"
              aria-label="toggle allows multiple devices"
              control={<Checkbox defaultChecked={false} />}
              label="Can the same user use multiple devices to join at the same time?"
            />
          </FormControl>
          <FormControl required component="fieldset" fullWidth>
            <FormControlLabel
              inputRef={register}
              name="zoom_enable_q_and_a"
              aria-label="toggle enable q and a"
              control={<Checkbox defaultChecked={false} />}
              label="Enable Zoom Q&A?"
            />
          </FormControl>
          <FormControl required component="fieldset" fullWidth>
            <FormControlLabel
              inputRef={register}
              name="zoom_enable_language_interpretation"
              aria-label="toggle enable language interpretation"
              control={<Checkbox defaultChecked={true} />}
              label="Enable language interpretation?"
            />
          </FormControl>
          <FormControl required component="fieldset" fullWidth>
            <FormControlLabel
              inputRef={register}
              name="zoom_enable_sign_language_interpretation"
              aria-label="toggle enable sign language interpretation"
              control={<Checkbox defaultChecked={true} />}
              label="Enable sign language interpretation?"
            />
          </FormControl>
          <div>
            <Button
              variant="contained"
              color="primary"
              disabled={submitting}
              type="submit"
              aria-label="create area"
            >
              Create
            </Button>
          </div>
        </form>
      </Page>
    </ZoomAreasBreadcrumbs>
  )
}

function useCreateArea() {
  const {client} = useOrganization()
  const {event} = useEvent()
  const url = api(`/events/${event.id}/areas`)

  return (data: CreateAreaData) => client.post<Area>(url, data)
}

const Title = withStyles({
  root: {
    marginBottom: spacing[4],
  },
})(Typography)
